// src/components/dashboard/LocationCard.js
import React from "react";
import { Typography, Box, useTheme, useMediaQuery } from "@mui/material";
import { styled } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MiniGoogleMap from "../../maps/minigooglemap.js";

// Enhanced glassmorphic location card
const GlassLocationCard = styled(Box)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  position: "relative",
  cursor: "pointer",
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.9)} 0%, 
    ${alpha(theme.palette.background.paper, 0.8)} 100%)`,
  backdropFilter: "blur(20px) saturate(1.1)",
  WebkitBackdropFilter: "blur(20px) saturate(1.1)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.15)}`,
  borderRadius: 20,
  overflow: "hidden",
  boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.primary.main, 0.3)}, 
      transparent)`,
    zIndex: 1,
  },

  "&::after": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 20% 30%, 
      ${alpha(theme.palette.primary.main, 0.02)} 0%, 
      transparent 50%), 
      radial-gradient(circle at 80% 70%, 
      ${alpha(theme.palette.secondary.main, 0.02)} 0%, 
      transparent 50%)`,
    pointerEvents: "none",
    zIndex: 0,
  },

  "& > *": {
    position: "relative",
    zIndex: 2,
  },

  // Mobile hover effects
  [theme.breakpoints.down("md")]: {
    borderRadius: 16,
    "&:hover": {
      boxShadow: `0 12px 40px ${alpha(theme.palette.common.black, 0.12)}`,
      transform: "translateY(-2px)",
      borderColor: alpha(theme.palette.primary.main, 0.2),
    },
    "&:active": {
      boxShadow: `0 4px 16px ${alpha(theme.palette.common.black, 0.1)}`,
      transform: "translateY(-1px)",
    },
  },

  // Desktop hover effects
  [theme.breakpoints.up("md")]: {
    "&:hover": {
      boxShadow: `0 16px 48px ${alpha(theme.palette.common.black, 0.15)}`,
      transform: "translateY(-4px)",
      borderColor: alpha(theme.palette.primary.main, 0.25),

      "&::before": {
        background: `linear-gradient(90deg, 
          transparent, 
          ${alpha(theme.palette.primary.main, 0.4)}, 
          ${alpha(theme.palette.secondary.main, 0.3)}, 
          transparent)`,
      },
    },
  },
}));

// Enhanced glassmorphic map container
const GlassMapContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  borderRadius: 16,
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.6)} 0%, 
    ${alpha(theme.palette.background.paper, 0.4)} 100%)`,
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  border: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
  boxShadow: `inset 0 2px 8px ${alpha(theme.palette.common.black, 0.05)}`,
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.primary.main, 0.2)}, 
      transparent)`,
    zIndex: 1,
  },

  "&:hover": {
    boxShadow: `inset 0 2px 8px ${alpha(theme.palette.common.black, 0.08)}, 
                0 4px 16px ${alpha(theme.palette.primary.main, 0.1)}`,
    borderColor: alpha(theme.palette.primary.main, 0.15),
  },

  [theme.breakpoints.down("md")]: {
    borderRadius: 12,
  },
}));

// Enhanced glassmorphic coordinates section
const GlassCoordinatesSection = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, 
    ${alpha(theme.palette.background.paper, 0.4)} 0%, 
    ${alpha(theme.palette.background.paper, 0.2)} 100%)`,
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  borderTop: `1px solid ${alpha(theme.palette.common.white, 0.1)}`,
  borderRadius: "0 0 16px 16px",
  position: "relative",
  marginTop: theme.spacing(1),
  padding: theme.spacing(1.5, 2),

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "1px",
    background: `linear-gradient(90deg, 
      transparent, 
      ${alpha(theme.palette.primary.main, 0.15)}, 
      transparent)`,
  },

  [theme.breakpoints.down("md")]: {
    borderRadius: "0 0 12px 12px",
    padding: theme.spacing(1, 1.5),
  },
}));

const LocationCard = ({ divesite }) => {
  const { latitude, longitude } = divesite.uddf.divesite.site.geography;

  // Mobile-friendly responsive design
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  // Responsive map height
  const getMapHeight = () => {
    if (isMobile) return "160px";
    if (isTablet) return "200px";
    return "220px";
  };

  // Responsive typography sizes
  const getTitleVariant = () => {
    if (isMobile) return "subtitle1";
    return "h6";
  };

  const getBodyVariant = () => {
    if (isMobile) return "body2";
    return "body2";
  };

  return (
    <GlassLocationCard
      sx={{
        padding: isMobile ? theme.spacing(2) : theme.spacing(3),
        width: "100%",
        maxWidth: isMobile ? "100%" : "400px",
      }}
    >
      {/* Enhanced site name with icon */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          mb: 2,
        }}
      >
        <LocationOnIcon
          sx={{
            color: "primary.main",
            fontSize: isMobile ? 20 : 24,
            mt: 0.5,
            filter: `drop-shadow(0 2px 4px ${alpha(
              theme.palette.primary.main,
              0.3
            )})`,
          }}
        />
        <Typography
          variant={getTitleVariant()}
          fontWeight="bold"
          color="primary.main"
          sx={{
            fontSize: isMobile ? "1.1rem" : "1.25rem",
            lineHeight: 1.3,
            wordBreak: "break-word",
            hyphens: "auto",
            textShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.1)}`,
          }}
        >
          {divesite.uddf.divesite.site.name}
        </Typography>
      </Box>

      {/* Enhanced map container */}
      <GlassMapContainer
        sx={{
          mb: 2,
          height: getMapHeight(),
        }}
      >
        <MiniGoogleMap
          lat={parseFloat(latitude?.toFixed(4))}
          lng={parseFloat(longitude?.toFixed(4))}
          markerName={divesite.uddf.divesite.site.name}
          height={getMapHeight()}
        />
      </GlassMapContainer>

      {/* Location description */}
      {divesite.uddf.divesite.site.geography.location && (
        <Typography
          variant={getBodyVariant()}
          sx={{
            mb: 1,
            fontSize: isMobile ? "0.875rem" : "0.875rem",
            lineHeight: 1.4,
            color: theme.palette.text.secondary,
            wordBreak: "break-word",
            fontStyle: "italic",
            opacity: 0.9,
          }}
        >
          {divesite.uddf.divesite.site.geography.location}
        </Typography>
      )}

      {/* Enhanced coordinates section */}
      <GlassCoordinatesSection>
        <Typography
          variant="caption"
          display="block"
          sx={{
            fontSize: isMobile ? "0.75rem" : "0.8rem",
            color: theme.palette.text.secondary,
            fontFamily: "monospace",
            minHeight: "20px",
            display: "flex",
            alignItems: "center",
            fontWeight: 500,
            letterSpacing: "0.5px",
          }}
        >
          <Box
            component="span"
            sx={{
              color: "primary.main",
              fontWeight: 600,
              mr: 0.5,
            }}
          >
            {isMobile ? "Coords:" : "Coordinates:"}
          </Box>
          {latitude?.toFixed(4)}, {longitude?.toFixed(4)}
        </Typography>
      </GlassCoordinatesSection>
    </GlassLocationCard>
  );
};

export default LocationCard;
